/**
 * THE single source of the Web Bluetooth §7 page-realm UUID machinery for every
 * TypeScript bundle: the npm core, the extension-injected surface
 * (`src/beacio/api/*`) and the standalone CDN polyfill (`src/cdn/beacio.ts`)
 * all import the three GATT assigned-numbers tables and the §7.1 resolver
 * (`canonicalUUID` + `resolveUUIDName`) FROM HERE. Because each shipped artifact
 * is webpack/tsup-bundled, this source module is inlined into each build, so
 * every artifact stays dependency-free while the tables + resolver exist ONCE.
 *
 * The three `registries:begin/end` table blocks are GENERATED by
 * `scripts/registries/generate.mjs` from the vendored WebBluetoothCG registry
 * files in `registries/` — the same single source of truth as
 * `Shared (Extension)/UUIDResolver.swift` (Swift cannot import TS, so it stays
 * a separate generated target). Regenerate with:
 *   node scripts/registries/generate.mjs
 *
 * The resolver semantics are the STRICT §7.1 form (spec: "A valid UUID is a
 * lower-case 128-bit string"): a string argument must be a valid lowercase
 * 128-bit UUID or a registered name (looked up case-folded because the tables
 * key the registry's mixed-case published spellings — e.g.
 * `magnetic_flux_density_2D` — in lowercase). The bare 4/8-hex and uppercase
 * leniency of the wire helpers (`src/beacio/api/uuid-normalization.ts`) is
 * intentionally NOT offered here.
 *
 * ARCHITECTURE BOUNDARY: This file holds ONLY the three GATT assigned-numbers
 * tables (services/characteristics/descriptors) + the Web Bluetooth §7.1
 * resolver functions (canonicalUUID, resolveUUIDName, hexToUUID). No other
 * logic. No new exports. Every consumer bundles this inline, so accumulation
 * here is paid N times across consumers.
 */

/** §7 base UUID suffix; a 16/32-bit alias replaces the top 32 bits. */
export const BLUETOOTH_BASE_UUID_SUFFIX = "-0000-1000-8000-00805f9b34fb";

/** §7 "A valid UUID is a string that matches [this] regexp" (lowercase only). */
export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// registries:begin gatt-services
// GENERATED from registries/gatt_assigned_services.txt by scripts/registries/generate.mjs — do not edit by hand.
export const GATT_ASSIGNED_SERVICES: Record<string, number> = {
  'generic_access': 0x1800,
  'generic_attribute': 0x1801,
  'immediate_alert': 0x1802,
  'link_loss': 0x1803,
  'tx_power': 0x1804,
  'current_time': 0x1805,
  'reference_time_update': 0x1806,
  'next_dst_change': 0x1807,
  'glucose': 0x1808,
  'health_thermometer': 0x1809,
  'device_information': 0x180A,
  'heart_rate': 0x180D,
  'phone_alert_status': 0x180E,
  'battery_service': 0x180F,
  'blood_pressure': 0x1810,
  'alert_notification': 0x1811,
  'human_interface_device': 0x1812,
  'scan_parameters': 0x1813,
  'running_speed_and_cadence': 0x1814,
  'automation_io': 0x1815,
  'cycling_speed_and_cadence': 0x1816,
  'cycling_power': 0x1818,
  'location_and_navigation': 0x1819,
  'environmental_sensing': 0x181A,
  'body_composition': 0x181B,
  'user_data': 0x181C,
  'weight_scale': 0x181D,
  'bond_management': 0x181E,
  'continuous_glucose_monitoring': 0x181F,
  'internet_protocol_support': 0x1820,
  'indoor_positioning': 0x1821,
  'pulse_oximeter': 0x1822,
  'http_proxy': 0x1823,
  'transport_discovery': 0x1824,
  'object_transfer': 0x1825,
  'fitness_machine': 0x1826,
  'mesh_provisioning': 0x1827,
  'mesh_proxy': 0x1828,
  'reconnection_configuration': 0x1829,
};
// registries:end gatt-services

// registries:begin gatt-characteristics
// GENERATED from registries/gatt_assigned_characteristics.txt by scripts/registries/generate.mjs — do not edit by hand.
export const GATT_ASSIGNED_CHARACTERISTICS: Record<string, number> = {
  'gap.device_name': 0x2A00,
  'gap.appearance': 0x2A01,
  'gap.peripheral_privacy_flag': 0x2A02,
  'gap.reconnection_address': 0x2A03,
  'gap.peripheral_preferred_connection_parameters': 0x2A04,
  'gatt.service_changed': 0x2A05,
  'alert_level': 0x2A06,
  'tx_power_level': 0x2A07,
  'date_time': 0x2A08,
  'day_of_week': 0x2A09,
  'day_date_time': 0x2A0A,
  'exact_time_100': 0x2A0B,
  'exact_time_256': 0x2A0C,
  'dst_offset': 0x2A0D,
  'time_zone': 0x2A0E,
  'local_time_information': 0x2A0F,
  'secondary_time_zone': 0x2A10,
  'time_with_dst': 0x2A11,
  'time_accuracy': 0x2A12,
  'time_source': 0x2A13,
  'reference_time_information': 0x2A14,
  'time_broadcast': 0x2A15,
  'time_update_control_point': 0x2A16,
  'time_update_state': 0x2A17,
  'glucose_measurement': 0x2A18,
  'battery_level': 0x2A19,
  'battery_power_state': 0x2A1A,
  'battery_level_state': 0x2A1B,
  'temperature_measurement': 0x2A1C,
  'temperature_type': 0x2A1D,
  'intermediate_temperature': 0x2A1E,
  'temperature_celsius': 0x2A1F,
  'temperature_fahrenheit': 0x2A20,
  'measurement_interval': 0x2A21,
  'boot_keyboard_input_report': 0x2A22,
  'system_id': 0x2A23,
  'model_number_string': 0x2A24,
  'serial_number_string': 0x2A25,
  'firmware_revision_string': 0x2A26,
  'hardware_revision_string': 0x2A27,
  'software_revision_string': 0x2A28,
  'manufacturer_name_string': 0x2A29,
  'ieee_11073-20601_regulatory_certification_data_list': 0x2A2A,
  'current_time': 0x2A2B,
  'magnetic_declination': 0x2A2C,
  'position_2d': 0x2A2F,
  'position_3d': 0x2A30,
  'scan_refresh': 0x2A31,
  'boot_keyboard_output_report': 0x2A32,
  'boot_mouse_input_report': 0x2A33,
  'glucose_measurement_context': 0x2A34,
  'blood_pressure_measurement': 0x2A35,
  'intermediate_cuff_pressure': 0x2A36,
  'heart_rate_measurement': 0x2A37,
  'body_sensor_location': 0x2A38,
  'heart_rate_control_point': 0x2A39,
  'removable': 0x2A3A,
  'service_required': 0x2A3B,
  'scientific_temperature_celsius': 0x2A3C,
  'string': 0x2A3D,
  'network_availability': 0x2A3E,
  'alert_status': 0x2A3F,
  'ringer_control_point': 0x2A40,
  'ringer_setting': 0x2A41,
  'alert_category_id_bit_mask': 0x2A42,
  'alert_category_id': 0x2A43,
  'alert_notification_control_point': 0x2A44,
  'unread_alert_status': 0x2A45,
  'new_alert': 0x2A46,
  'supported_new_alert_category': 0x2A47,
  'supported_unread_alert_category': 0x2A48,
  'blood_pressure_feature': 0x2A49,
  'hid_information': 0x2A4A,
  'report_map': 0x2A4B,
  'hid_control_point': 0x2A4C,
  'report': 0x2A4D,
  'protocol_mode': 0x2A4E,
  'scan_interval_window': 0x2A4F,
  'pnp_id': 0x2A50,
  'glucose_feature': 0x2A51,
  'record_access_control_point': 0x2A52,
  'rsc_measurement': 0x2A53,
  'rsc_feature': 0x2A54,
  'sc_control_point': 0x2A55,
  'digital': 0x2A56,
  'digital_output': 0x2A57,
  'analog': 0x2A58,
  'analog_output': 0x2A59,
  'aggregate': 0x2A5A,
  'csc_measurement': 0x2A5B,
  'csc_feature': 0x2A5C,
  'sensor_location': 0x2A5D,
  'plx_spot_check_measurement': 0x2A5E,
  'plx_continuous_measurement': 0x2A5F,
  'plx_features': 0x2A60,
  'pulse_oximetry_control_point': 0x2A62,
  'cycling_power_measurement': 0x2A63,
  'cycling_power_vector': 0x2A64,
  'cycling_power_feature': 0x2A65,
  'cycling_power_control_point': 0x2A66,
  'location_and_speed': 0x2A67,
  'navigation': 0x2A68,
  'position_quality': 0x2A69,
  'ln_feature': 0x2A6A,
  'ln_control_point': 0x2A6B,
  'elevation': 0x2A6C,
  'pressure': 0x2A6D,
  'temperature': 0x2A6E,
  'humidity': 0x2A6F,
  'true_wind_speed': 0x2A70,
  'true_wind_direction': 0x2A71,
  'apparent_wind_speed': 0x2A72,
  'apparent_wind_direction': 0x2A73,
  'gust_factor': 0x2A74,
  'pollen_concentration': 0x2A75,
  'uv_index': 0x2A76,
  'irradiance': 0x2A77,
  'rainfall': 0x2A78,
  'wind_chill': 0x2A79,
  'heat_index': 0x2A7A,
  'dew_point': 0x2A7B,
  'descriptor_value_changed': 0x2A7D,
  'aerobic_heart_rate_lower_limit': 0x2A7E,
  'aerobic_threshold': 0x2A7F,
  'age': 0x2A80,
  'anaerobic_heart_rate_lower_limit': 0x2A81,
  'anaerobic_heart_rate_upper_limit': 0x2A82,
  'anaerobic_threshold': 0x2A83,
  'aerobic_heart_rate_upper_limit': 0x2A84,
  'date_of_birth': 0x2A85,
  'date_of_threshold_assessment': 0x2A86,
  'email_address': 0x2A87,
  'fat_burn_heart_rate_lower_limit': 0x2A88,
  'fat_burn_heart_rate_upper_limit': 0x2A89,
  'first_name': 0x2A8A,
  'five_zone_heart_rate_limits': 0x2A8B,
  'gender': 0x2A8C,
  'heart_rate_max': 0x2A8D,
  'height': 0x2A8E,
  'hip_circumference': 0x2A8F,
  'last_name': 0x2A90,
  'maximum_recommended_heart_rate': 0x2A91,
  'resting_heart_rate': 0x2A92,
  'sport_type_for_aerobic_and_anaerobic_thresholds': 0x2A93,
  'three_zone_heart_rate_limits': 0x2A94,
  'two_zone_heart_rate_limit': 0x2A95,
  'vo2_max': 0x2A96,
  'waist_circumference': 0x2A97,
  'weight': 0x2A98,
  'database_change_increment': 0x2A99,
  'user_index': 0x2A9A,
  'body_composition_feature': 0x2A9B,
  'body_composition_measurement': 0x2A9C,
  'weight_measurement': 0x2A9D,
  'weight_scale_feature': 0x2A9E,
  'user_control_point': 0x2A9F,
  'magnetic_flux_density_2d': 0x2AA0,
  'magnetic_flux_density_3d': 0x2AA1,
  'language': 0x2AA2,
  'barometric_pressure_trend': 0x2AA3,
  'bond_management_control_point': 0x2AA4,
  'bond_management_feature': 0x2AA5,
  'gap.central_address_resolution_support': 0x2AA6,
  'cgm_measurement': 0x2AA7,
  'cgm_feature': 0x2AA8,
  'cgm_status': 0x2AA9,
  'cgm_session_start_time': 0x2AAA,
  'cgm_session_run_time': 0x2AAB,
  'cgm_specific_ops_control_point': 0x2AAC,
  'indoor_positioning_configuration': 0x2AAD,
  'latitude': 0x2AAE,
  'longitude': 0x2AAF,
  'local_north_coordinate': 0x2AB0,
  'local_east_coordinate': 0x2AB1,
  'floor_number': 0x2AB2,
  'altitude': 0x2AB3,
  'uncertainty': 0x2AB4,
  'location_name': 0x2AB5,
  'uri': 0x2AB6,
  'http_headers': 0x2AB7,
  'http_status_code': 0x2AB8,
  'http_entity_body': 0x2AB9,
  'http_control_point': 0x2ABA,
  'https_security': 0x2ABB,
  'tds_control_point': 0x2ABC,
  'ots_feature': 0x2ABD,
  'object_name': 0x2ABE,
  'object_type': 0x2ABF,
  'object_size': 0x2AC0,
  'object_first_created': 0x2AC1,
  'object_last_modified': 0x2AC2,
  'object_id': 0x2AC3,
  'object_properties': 0x2AC4,
  'object_action_control_point': 0x2AC5,
  'object_list_control_point': 0x2AC6,
  'object_list_filter': 0x2AC7,
  'object_changed': 0x2AC8,
  'resolvable_private_address_only': 0x2AC9,
  'fitness_machine_feature': 0x2ACC,
  'treadmill_data': 0x2ACD,
  'cross_trainer_data': 0x2ACE,
  'step_climber_data': 0x2ACF,
  'stair_climber_data': 0x2AD0,
  'rower_data': 0x2AD1,
  'indoor_bike_data': 0x2AD2,
  'training_status': 0x2AD3,
  'supported_speed_range': 0x2AD4,
  'supported_inclination_range': 0x2AD5,
  'supported_resistance_level_range': 0x2AD6,
  'supported_heart_rate_range': 0x2AD7,
  'supported_power_range': 0x2AD8,
  'fitness_machine_control_point': 0x2AD9,
  'fitness_machine_status': 0x2ADA,
  'date_utc': 0x2AED,
};
// registries:end gatt-characteristics

// registries:begin gatt-descriptors
// GENERATED from registries/gatt_assigned_descriptors.txt by scripts/registries/generate.mjs — do not edit by hand.
export const GATT_ASSIGNED_DESCRIPTORS: Record<string, number> = {
  'gatt.characteristic_extended_properties': 0x2900,
  'gatt.characteristic_user_description': 0x2901,
  'gatt.client_characteristic_configuration': 0x2902,
  'gatt.server_characteristic_configuration': 0x2903,
  'gatt.characteristic_presentation_format': 0x2904,
  'gatt.characteristic_aggregate_format': 0x2905,
  'valid_range': 0x2906,
  'external_report_reference': 0x2907,
  'report_reference': 0x2908,
  'number_of_digitals': 0x2909,
  'value_trigger_setting': 0x290A,
  'es_configuration': 0x290B,
  'es_measurement': 0x290C,
  'es_trigger_setting': 0x290D,
  'time_trigger_setting': 0x290E,
};
// registries:end gatt-descriptors

/** Expand a validated 16/32-bit alias into the canonical lowercase 128-bit UUID. */
function hexToUUID(hex: number): string {
  return hex.toString(16).padStart(8, "0") + BLUETOOTH_BASE_UUID_SUFFIX;
}

/**
 * `BluetoothUUID.canonicalUUID(alias)` (§7): expand a 16/32-bit alias into the
 * canonical lowercase 128-bit UUID string. Applies the WebIDL `[EnforceRange]
 * unsigned long` conversion: ToNumber, reject non-finite, truncate toward zero,
 * reject outside [0, 2^32 − 1]. Fractional/numeric-string inputs CONVERT.
 */
export function canonicalUUID(alias: number): string {
  const converted = Number(alias);
  if (!Number.isFinite(converted)) {
    throw new TypeError(
      `Failed to execute 'canonicalUUID' on 'BluetoothUUID': ` +
        `Value is not a valid unsigned long: ${alias}`,
    );
  }
  const truncated = Math.trunc(converted);
  if (truncated < 0 || truncated > 0xffffffff) {
    throw new TypeError(
      `Failed to execute 'canonicalUUID' on 'BluetoothUUID': ` +
        `Value is not a valid unsigned long: ${alias}`,
    );
  }
  // `+ 0` normalizes -0 (from truncating e.g. -0.5) to 0.
  return hexToUUID(truncated + 0);
}

/**
 * §7.1 ResolveUUIDName, scoped to a single GATT assigned-numbers table.
 * Names are table-scoped because the registries reuse names across categories
 * (`current_time` is service 0x1805 AND characteristic 0x2A2B), so
 * `getService` / `getCharacteristic` / `getDescriptor` each consult only their
 * own table.
 *
 *  1. unsigned long           → canonicalUUID(name)
 *  2. valid lowercase UUID    → pass through
 *  3. registered name         → canonicalUUID(alias) (case-folded lookup)
 *  4. otherwise               → throw a TypeError
 *
 * `String(name)` coerces page-realm non-string garbage so it stringifies (and
 * misses) rather than dying on `.toLowerCase`.
 */
export function resolveUUIDName(
  name: string | number,
  table: Record<string, number>,
  getter: string,
): string {
  if (typeof name === "number") return canonicalUUID(name);
  const value = String(name);
  if (UUID_RE.test(value)) return value;
  const alias = table[value.toLowerCase()];
  if (alias !== undefined) return hexToUUID(alias);
  throw new TypeError(
    `Failed to execute '${getter}' on 'BluetoothUUID': Invalid UUID or registry name: "${value}"`,
  );
}
